import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import dayjs from 'dayjs';

import { ASSET_TYPE_PLURALS, ASSET_TYPES } from '@/shared/api/openmetadata';
import { ASSET_TAB_CONFIGS, type TabKey } from '@/shared/lib/asset';
import { extractModelNameFromFQN } from '@/shared/lib/mlflow';
import { registeredModelKeys } from '@/shared/lib/query/queryKeys';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { TabLabel } from '@/shared/ui/TabLabel';

import { useModelVersions } from '@/entities/model-version';
import { getRegisteredModel } from '@/entities/registered-model';

import {
  useDatabaseSchemas,
  useDataInsights,
  useLineage,
  useSampleData,
  useSchema,
  useServiceContainers,
  useServiceDatabases,
  useServiceMLModels,
  useTables,
} from '@/features/asset-detail';
import type { AssetBaseInfo } from '@/features/asset-detail/api/baseApi';

import {
  ContainersTab,
  DatabasesTab,
  FeaturesTab,
  FilesTab,
  InsightsTab,
  LineageTab,
  MLModelsTab,
  SampleDataTab,
  SchemasTab,
  SchemaTab,
  TablesTab,
  VersionsTab,
} from '@/widgets/asset-detail';

import styles from './AssetDetailContent.module.css';

type AssetDetailContentProps = {
  entityType: string;
  fqn: string;
  entityId: string;
  currentTab: string;
  assetData: AssetBaseInfo;
  isLineageFullscreen?: boolean;
  onToggleLineageFullscreen?: () => void;
};

export const AssetDetailContent: React.FC<AssetDetailContentProps> = ({
  entityType,
  fqn,
  entityId,
  currentTab,
  assetData,
  isLineageFullscreen = false,
  onToggleLineageFullscreen,
}) => {
  const navigate = useNavigate();
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const shouldFetchSchema = currentTab === 'columns' || currentTab === 'sample-data';
  const {
    data: schemaData,
    isLoading: isSchemaLoading,
    isError: isSchemaError,
    error: schemaError,
    refetch: refetchSchema,
  } = useSchema(entityType, fqn, shouldFetchSchema);

  const shouldFetchSampleData = currentTab === 'columns' || currentTab === 'sample-data';
  const {
    data: sampleData,
    isLoading: isSampleDataLoading,
    isError: isSampleDataError,
    error: sampleDataError,
    refetch: refetchSampleData,
  } = useSampleData(entityType, entityId, shouldFetchSampleData);

  const shouldFetchLineage = currentTab === 'lineage';
  const {
    data: lineageData,
    isLoading: isLineageLoading,
    isError: isLineageError,
    error: lineageError,
    refetch: refetchLineage,
  } = useLineage(entityType, fqn, shouldFetchLineage);

  const shouldFetchSchemas = currentTab === 'schemas';
  const {
    data: schemasData,
    isLoading: isSchemasLoading,
    isError: isSchemasError,
    error: schemasError,
    refetch: refetchSchemas,
  } = useDatabaseSchemas(fqn, includeDeleted, shouldFetchSchemas);

  const shouldFetchTables = currentTab === 'tables';
  const {
    data: tablesData,
    isLoading: isTablesLoading,
    isError: isTablesError,
    error: tablesError,
    refetch: refetchTables,
  } = useTables(fqn, includeDeleted, shouldFetchTables);

  const serviceType = useMemo(() => {
    if (entityType !== ASSET_TYPES.SERVICE && entityType !== ASSET_TYPE_PLURALS.SERVICE) {
      return null;
    }
    return fqn.split('/')[0];
  }, [entityType, fqn]);

  const serviceName = useMemo(() => {
    return entityType === ASSET_TYPES.SERVICE || entityType === ASSET_TYPE_PLURALS.SERVICE
      ? fqn.split('/')[1] || assetData.name
      : assetData.name;
  }, [entityType, fqn, assetData.name]);

  const shouldFetchContainers =
    currentTab === 'containers' ||
    (currentTab === 'insights' && (serviceType === 'storageServices' || serviceType === 'storageService'));

  const {
    data: containersData,
    isLoading: isContainersLoading,
    isError: isContainersError,
    error: containersError,
    refetch: refetchContainers,
  } = useServiceContainers(serviceName, { includeDeleted }, shouldFetchContainers);

  const shouldFetchDatabases =
    currentTab === 'databases' ||
    (currentTab === 'insights' && (serviceType === 'databaseServices' || serviceType === 'databaseService'));

  const {
    data: databasesData,
    isLoading: isDatabasesLoading,
    isError: isDatabasesError,
    error: databasesError,
    refetch: refetchDatabases,
  } = useServiceDatabases(serviceName, { includeDeleted }, shouldFetchDatabases);

  const shouldFetchMLModels =
    currentTab === 'mlmodels' ||
    (currentTab === 'insights' && (serviceType === 'mlmodelServices' || serviceType === 'mlmodelService'));

  const {
    data: mlmodelsData,
    isLoading: isMLModelsLoading,
    isError: isMLModelsError,
    error: mlmodelsError,
    refetch: refetchMLModels,
  } = useServiceMLModels(serviceName, { includeDeleted }, shouldFetchMLModels);

  const shouldFetchInsights = currentTab === 'insights';
  const [dateRange] = useState(() => {
    const end = dayjs().startOf('day').valueOf();
    const start = dayjs().subtract(6, 'day').startOf('day').valueOf();
    return { startDate: start, endDate: end };
  });

  const { data: insightsData, isLoading: isInsightsLoading } = useDataInsights(
    serviceName,
    dateRange.startDate,
    dateRange.endDate,
    shouldFetchInsights
  );

  const shouldFetchVersions = currentTab === 'versions' && entityType === ASSET_TYPES.MLMODEL;
  const modelName = useMemo(() => {
    return entityType === ASSET_TYPES.MLMODEL ? extractModelNameFromFQN(fqn) : '';
  }, [entityType, fqn]);

  const versionsFilter = useMemo(() => {
    if (!modelName) return '';

    const VALID_MODEL_NAME_PATTERN = /^[a-zA-Z0-9_.-]+$/;
    if (!VALID_MODEL_NAME_PATTERN.test(modelName)) {
      console.warn('[AssetDetailContent] Invalid model name detected:', modelName);
      return '';
    }

    const escapedModelName = modelName.replace(/'/g, "''");
    return `name='${escapedModelName}'`;
  }, [modelName]);

  const {
    data: versionsData,
    isLoading: isVersionsLoading,
    isError: isVersionsError,
    error: versionsError,
    refetch: refetchVersions,
  } = useModelVersions(
    {
      filter: versionsFilter,
      order_by: ['creation_timestamp DESC'],
    },
    shouldFetchVersions && !!versionsFilter
  );

  const { data: registeredModelData, isLoading: isRegisteredModelLoading } = useQuery({
    queryKey: registeredModelKeys.registeredModelDetail(modelName),
    queryFn: () => getRegisteredModel({ name: modelName }),
    enabled: shouldFetchVersions && !!modelName,
    staleTime: 5 * 60 * 1000,
  });

  const versionsWithAliases = useMemo(() => {
    if (!versionsData?.model_versions || !registeredModelData?.registered_model?.aliases) {
      return versionsData?.model_versions || [];
    }

    const aliasesMap = new Map<string, string[]>();
    registeredModelData.registered_model.aliases.forEach((alias) => {
      const existingAliases = aliasesMap.get(alias.version) || [];
      existingAliases.push(alias.alias);
      aliasesMap.set(alias.version, existingAliases);
    });

    return versionsData.model_versions.map((version) => ({
      ...version,
      aliases: aliasesMap.get(version.version) || [],
    }));
  }, [versionsData, registeredModelData]);

  const handleTabChange = useCallback(
    (activeKey: string) => {
      if (entityType === ASSET_TYPES.SERVICE || entityType === ASSET_TYPE_PLURALS.SERVICE) {
        const [serviceType, serviceName] = fqn.split('/');
        navigate(`/service/${serviceType}/${serviceName}/${activeKey}`);
      } else {
        navigate(`/${entityType}/${fqn}/${activeKey}`);
      }
    },
    [navigate, entityType, fqn]
  );

  const TAB_COUNT_GETTERS = useMemo<Partial<Record<TabKey, () => number>>>(
    () => ({
      columns: () => schemaData?.length || 0,
      'sample-data': () => sampleData?.rows?.length || 0,
      schemas: () => (schemasData && 'data' in schemasData ? schemasData.data?.length || 0 : 0),
      tables: () => (tablesData && 'data' in tablesData ? tablesData.data?.length || 0 : 0),
      containers: () => containersData?.data?.length || 0,
      databases: () => databasesData?.data?.length || 0,
      mlmodels: () => mlmodelsData?.data?.length || 0,
      versions: () => versionsWithAliases?.length || 0,
    }),
    [schemaData, sampleData, schemasData, tablesData, containersData, databasesData, mlmodelsData, versionsWithAliases]
  );

  const TAB_LOADING_GETTERS = useMemo<Partial<Record<TabKey, () => boolean>>>(
    () => ({
      columns: () => isSchemaLoading,
      'sample-data': () => isSampleDataLoading,
      schemas: () => isSchemasLoading,
      tables: () => isTablesLoading,
      containers: () => isContainersLoading,
      databases: () => isDatabasesLoading,
      mlmodels: () => isMLModelsLoading,
      versions: () => isVersionsLoading || isRegisteredModelLoading,
    }),
    [
      isSchemaLoading,
      isSampleDataLoading,
      isSchemasLoading,
      isTablesLoading,
      isContainersLoading,
      isDatabasesLoading,
      isMLModelsLoading,
      isVersionsLoading,
      isRegisteredModelLoading,
    ]
  );

  const getTabCount = useCallback(
    (tabKey: TabKey): number => {
      return TAB_COUNT_GETTERS[tabKey]?.() || 0;
    },
    [TAB_COUNT_GETTERS]
  );

  const getTabLoading = useCallback(
    (tabKey: TabKey): boolean => {
      return TAB_LOADING_GETTERS[tabKey]?.() || false;
    },
    [TAB_LOADING_GETTERS]
  );

  const renderColumnsTab = useCallback(
    () => (
      <SchemaTab
        columns={schemaData || []}
        tableConstraints={assetData.tableConstraints}
        tableFqn={fqn}
        entityType={entityType}
        isLoading={isSchemaLoading}
        isError={isSchemaError}
        error={schemaError}
        onRetry={refetchSchema}
      />
    ),
    [
      schemaData,
      assetData.tableConstraints,
      fqn,
      entityType,
      isSchemaLoading,
      isSchemaError,
      schemaError,
      refetchSchema,
    ]
  );

  const renderSampleDataTab = useCallback(
    () => (
      <SampleDataTab
        sampleData={sampleData ?? null}
        schemaColumns={schemaData || undefined}
        tableName={fqn.split('.').pop() || 'table'}
        isLoading={isSampleDataLoading}
        isError={isSampleDataError}
        error={sampleDataError}
        onRetry={refetchSampleData}
      />
    ),
    [sampleData, schemaData, fqn, isSampleDataLoading, isSampleDataError, sampleDataError, refetchSampleData]
  );

  const renderLineageTab = useCallback(
    () => (
      <LineageTab
        lineageData={lineageData || undefined}
        isFullscreen={isLineageFullscreen}
        onToggleFullscreen={onToggleLineageFullscreen}
        isLoading={isLineageLoading}
        isError={isLineageError}
        error={lineageError}
        onRetry={refetchLineage}
      />
    ),
    [
      lineageData,
      isLineageFullscreen,
      onToggleLineageFullscreen,
      isLineageLoading,
      isLineageError,
      lineageError,
      refetchLineage,
    ]
  );

  const renderFilesTab = useCallback(() => <FilesTab entityId={entityId} containerFqn={fqn} />, [entityId, fqn]);

  const renderSchemasTab = useCallback(
    () => (
      <SchemasTab
        schemas={schemasData && 'data' in schemasData ? schemasData.data || [] : []}
        isLoading={isSchemasLoading}
        isError={isSchemasError}
        error={schemasError}
        onRetry={refetchSchemas}
        onDeletedToggle={setIncludeDeleted}
        includeDeleted={includeDeleted}
      />
    ),
    [schemasData, isSchemasLoading, isSchemasError, schemasError, refetchSchemas, includeDeleted]
  );

  const renderTablesTab = useCallback(
    () => (
      <TablesTab
        tables={tablesData && 'data' in tablesData ? tablesData.data || [] : []}
        isLoading={isTablesLoading}
        isError={isTablesError}
        error={tablesError}
        onRetry={refetchTables}
        onDeletedToggle={setIncludeDeleted}
        includeDeleted={includeDeleted}
      />
    ),
    [tablesData, isTablesLoading, isTablesError, tablesError, refetchTables, includeDeleted]
  );

  const renderContainersTab = useCallback(
    () => (
      <ContainersTab
        containers={containersData?.data || []}
        isLoading={isContainersLoading}
        isError={isContainersError}
        error={containersError}
        onRetry={refetchContainers}
        onDeletedToggle={setIncludeDeleted}
        includeDeleted={includeDeleted}
      />
    ),
    [containersData, isContainersLoading, isContainersError, containersError, refetchContainers, includeDeleted]
  );

  const renderDatabasesTab = useCallback(
    () => (
      <DatabasesTab
        databases={databasesData?.data || []}
        isLoading={isDatabasesLoading}
        isError={isDatabasesError}
        error={databasesError}
        onRetry={refetchDatabases}
        onDeletedToggle={setIncludeDeleted}
        includeDeleted={includeDeleted}
      />
    ),
    [databasesData, isDatabasesLoading, isDatabasesError, databasesError, refetchDatabases, includeDeleted]
  );

  const renderMLModelsTab = useCallback(
    () => (
      <MLModelsTab
        mlmodels={mlmodelsData?.data || []}
        isLoading={isMLModelsLoading}
        isError={isMLModelsError}
        error={mlmodelsError}
        onRetry={refetchMLModels}
        onDeletedToggle={setIncludeDeleted}
        includeDeleted={includeDeleted}
      />
    ),
    [mlmodelsData, isMLModelsLoading, isMLModelsError, mlmodelsError, refetchMLModels, includeDeleted]
  );

  const renderInsightsTab = useCallback(() => {
    if (isContainersError || isDatabasesError) {
      return (
        <ErrorFallback
          error={containersError || databasesError}
          description="인사이트에 필요한 자산 정보를 불러오지 못했습니다. 다시 시도해 주세요."
          resetErrorBoundary={() => {
            refetchContainers();
            refetchDatabases();
          }}
        />
      );
    }

    const assets = containersData?.data || databasesData?.data || [];
    const assetTypeCounts: Array<{ type: string; count: number }> = [];

    if (containersData?.data && containersData.data.length > 0) {
      assetTypeCounts.push({
        type: 'container',
        count: containersData.data.length,
      });
    }

    if (databasesData?.data && databasesData.data.length > 0) {
      assetTypeCounts.push({
        type: 'database',
        count: databasesData.data.length,
      });
    }

    return (
      <InsightsTab
        insightsData={insightsData || undefined}
        totalAssets={assets.length}
        assetTypeCounts={assetTypeCounts}
        isInsightsLoading={isInsightsLoading}
        isAssetsLoading={isContainersLoading || isDatabasesLoading}
      />
    );
  }, [
    containersData,
    databasesData,
    insightsData,
    isInsightsLoading,
    isContainersLoading,
    isDatabasesLoading,
    isContainersError,
    isDatabasesError,
    containersError,
    databasesError,
    refetchContainers,
    refetchDatabases,
  ]);

  const renderFeaturesTab = useCallback(() => <FeaturesTab />, []);

  const renderVersionsTab = useCallback(
    () => (
      <VersionsTab
        versions={versionsWithAliases}
        modelFqn={fqn}
        isLoading={isVersionsLoading || isRegisteredModelLoading}
        isError={isVersionsError}
        error={versionsError}
        onRetry={refetchVersions}
      />
    ),
    [
      versionsWithAliases,
      fqn,
      isVersionsLoading,
      isRegisteredModelLoading,
      isVersionsError,
      versionsError,
      refetchVersions,
    ]
  );

  const TAB_CONTENT_HANDLERS = useMemo<Partial<Record<TabKey, () => React.ReactNode>>>(
    () => ({
      columns: renderColumnsTab,
      'sample-data': renderSampleDataTab,
      lineage: renderLineageTab,
      files: renderFilesTab,
      schemas: renderSchemasTab,
      tables: renderTablesTab,
      containers: renderContainersTab,
      databases: renderDatabasesTab,
      mlmodels: renderMLModelsTab,
      insights: renderInsightsTab,
      features: renderFeaturesTab,
      versions: renderVersionsTab,
    }),
    [
      renderColumnsTab,
      renderSampleDataTab,
      renderLineageTab,
      renderFilesTab,
      renderSchemasTab,
      renderTablesTab,
      renderContainersTab,
      renderDatabasesTab,
      renderMLModelsTab,
      renderInsightsTab,
      renderFeaturesTab,
      renderVersionsTab,
    ]
  );

  const getTabContent = useCallback(
    (tabKey: TabKey): React.ReactNode => {
      const handler = TAB_CONTENT_HANDLERS[tabKey];
      return handler ? handler() : null;
    },
    [TAB_CONTENT_HANDLERS]
  );

  const tabConfigs = useMemo(() => {
    const baseConfigs = ASSET_TAB_CONFIGS[entityType as keyof typeof ASSET_TAB_CONFIGS];
    if (!baseConfigs) return [];

    if (entityType === ASSET_TYPES.SERVICE && serviceType) {
      const SERVICE_TAB_CONFIGS_MAP: Record<string, typeof baseConfigs> = {
        databaseServices: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'databases', label: '데이터베이스', showCount: true },
        ],
        databaseService: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'databases', label: '데이터베이스', showCount: true },
        ],
        storageServices: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'containers', label: '버킷', showCount: true },
        ],
        storageService: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'containers', label: '버킷', showCount: true },
        ],
        mlmodelServices: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'mlmodels', label: 'ML 모델', showCount: true },
        ],
        mlmodelService: [
          { key: 'insights', label: '인사이트', showCount: false },
          { key: 'mlmodels', label: 'ML 모델', showCount: true },
        ],
      };

      return SERVICE_TAB_CONFIGS_MAP[serviceType] || baseConfigs;
    }

    return baseConfigs;
  }, [entityType, serviceType]);

  const tabItems: TabsProps['items'] = useMemo(
    () =>
      tabConfigs.map((config) => ({
        key: config.key,
        label: (
          <TabLabel
            label={config.label}
            count={getTabCount(config.key)}
            showCount={config.showCount}
            active={currentTab === config.key}
            isLoading={getTabLoading(config.key)}
          />
        ),
        children: getTabContent(config.key),
      })),
    [tabConfigs, getTabCount, getTabContent, currentTab, getTabLoading]
  );

  return (
    <div className={`${styles.assetDetailContent} ${isLineageFullscreen ? styles.fullscreen : ''}`}>
      <Tabs activeKey={currentTab} items={tabItems} onChange={handleTabChange} />
    </div>
  );
};
